<template>
  <div class="source-content legacy-graphics">
    <div class="filter-bar">
      <div class="tb-form-group">
        <search-filter
          :value="search"
          search-only
          class="flex-grow"
          data-test="producer-assets-savedGraphics-searchFilter"
          @input="changeGraphicsSearch"
          @update:selected="changeGraphicTags"
        />

        <button
          class="tb-btn tb-btn-contained high-em margin-left-sm"
          data-entity="producer-assets-createSavedGraphic"
          data-test="producer-assets-createSavedGraphic"
          @click="showTemplatePicker = true"
        >
          Create
        </button>
      </div>
    </div>

    <draggable
      v-if="filteredGraphics.length"
      class="source-list"
      v-model="filteredGraphics"
      :clone="clone"
      :sort="false"
      animation="200"
      :group="{ name: 'timeline', pull: 'clone', put: false }"
      @start="startDrag"
      @end="$store.dispatch('clearDragType')"
      revert-on-spill="true"
      data-test="producer-assets-displayElements-list"
    >
      <div
        v-for="(graphic, index) in filteredGraphics"
        :key="graphic._id"
        class="source-item legacy-graphic"
        :data-template-id="graphic._id"
        data-entity="producer-assets-savedGraphic"
        :data-test="`producer-assets-element:${graphic._id}`"
      >
        <div class="tb-checkbox">
          <input
            type="checkbox"
            :id="graphic._id"
            :value="graphic"
            v-model="selected"
            data-entity="producer-assets-toggleSavedGraphicSelection"
            :data-test="`producer-assets-element-checkBox:${graphic._id}`"
          >

          <label class="checkbox" :for="graphic._id" />
        </div>

        <div class="source-item-wrapper overflow-visible">
          <img
            class="preview margin-right-xs"
            v-if="lookupTable[graphic.template_id]"
            :src="lookupTable[graphic.template_id].preview_url"
            :data-test="`producer-assets-element-preview:${graphic._id}`"
          >

          <div class="source-title title-text" :title="graphic.title" :data-test="`producer-assets-element-title:${graphic._id}`">
            {{ graphic.title }}
            <br>

            <div
              v-if="lookupTable[graphic.template_id]"
              class="description-text capitalize"
              :data-test="`producer-assets-element-description:${graphic.template_id}`"
            >
              {{ lookupTable[graphic.template_id].title }}
            </div>
          </div>

          <div class="source-actions margin-left-xxs">
            <button
              class="tb-btn tb-btn-contained sm medium-em add-to-playlist"
              title="Add to playlist"
              data-entity="producer-assets-addSavedGraphicToPlaylist"
              :data-test="`producer-assets-element-addToPlaylist:${graphic._id}`"
              @click="addToPlaylist(graphic, 'Saved Graphic')"
            >
              <span class="icon icon-plus" />
            </button>

            <dropdown position="left" class="more-actions margin-left-xxs" ref="dropdown">
              <template slot="trigger">
                <button
                  class="tb-btn tb-btn-text sm medium-em"
                  title="More actions"
                  data-entity="producer-assets-toggleSavedGraphicContextMenu"
                  :data-test="`producer-assets-element-context:${graphic._id}`"
                >
                  <span class="icon icon-more-vertical"></span>
                </button>
              </template>

              <template slot="dropdown-body">
                <button
                  class="dropdown-list-item"
                  data-entity="producer-assets-duplicateSavedGraphic"
                  @click="duplicateGraphic(graphic, index)"
                >
                  <span class="dropdown-list-item-label">
                    Duplicate
                  </span>
                </button>

                <button
                  class="dropdown-list-item"
                  data-entity="producer-assets-deleteSavedGraphic"
                  @click="deleteConfirm(graphic)"
                >
                  <span class="dropdown-list-item-label warning">
                    Delete
                  </span>
                </button>
              </template>
            </dropdown>
          </div>
        </div>
      </div>
    </draggable>

    <div
      v-if="selected.length"
      class="flex content-end items-center padding-md"
      data-test="producer-assets-selected"
    >
      <p
        class="text-primary margin-bottom-none margin-right-sm"
        data-test="producer-assets-selected-count"
      >
        {{ selected.length }} selected
      </p>

      <tgb-split-button
        :options="splitButtonOptions"
        :disabled="!selected.length"
        split-button-label="Add group"
        data-entity="producer-assets-addSelectedTemplatesToPlaylist"
        data-test="producer-assets-addElementsToPlaylist"
        @click="addSelectedToPlaylist('Graphics')"
      >
        Add to playlist
      </tgb-split-button>
    </div>

    <template-modal
      v-if="showTemplatePicker"
      data-test="producer-assets-templatePicker-modal"
      @close="showTemplatePicker = false"
    />

    <alert
      v-if="showAlert"
      footerConfirm="true"
      data-test="producer-assets-deleteGraphicAlert"
      @confirm="deleteGraphic"
      @close="showAlert = false"
    >
      <h2>Are you sure?</h2>
      <p>This will delete this graphic and will break any production that is using it.</p>
    </alert>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { pick } from 'lodash-es';
import * as uuid from 'uuid';

import SearchFilter from '@/js/shared/SearchFilter.vue';
import AlertComponent from '../../components/alert/Alert.vue';
import tabMixins from '../../mixins/tab-mixins';
import TemplateModalComponent from '../../components/template-modal/TemplateModal.vue';
import SplitButton from '../../components/split-button/TgbSplitButton.vue';

import toast from '../../components/toast/toast';

export default {
  mixins: [tabMixins],

  components: {
    SearchFilter,
    alert: AlertComponent,
    'template-modal': TemplateModalComponent,
    'tgb-split-button': SplitButton,
  },

  data() {
    return {
      showAlert: false,
      showTemplatePicker: false,
      selectedGraphic: null,
    };
  },

  created() {
    this.$store.dispatch('getTemplates');

    this.$store.dispatch('getGraphics').catch((err) => {
      console.error('Error loading saved graphics:', err);

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: 'Failed to load saved graphics.',
      });
    });

    window.addEventListener('message', (e) => {
      if (e.origin !== process.env.TAGBOARD_GRAPHICS_URL) {
        return;
      }

      if (typeof e.data === 'string' && e.data.indexOf('tgbTemplateUpdate') === 0) {
        const temp = JSON.parse(e.data.split('|')[1]);
        this.$store.commit('EDIT_TEMPLATE', temp);
      }
    }, false);
  },

  beforeDestroy() {
    // window.removeEventListener('message', (e) => {}, false);
    this.$store.dispatch('setGraphics', []);
  },

  computed: {
    ...mapState({
      isLoading: (state) => state.graphics.isLoading,
      search: (state) => state.graphics.search,
      graphics: (state) => state.graphics.graphics,
      graphicModalOpen: (state) => state.graphics.graphicModalOpen,
      assetModalOpen: (state) => state.graphics.assetModalOpen,
    }),

    ...mapGetters(['sortedGraphicsTags']),

    filteredGraphics() {
      return this.graphics.filter((graphic) => (
        this.search
        ? (
          graphic.title.toLowerCase().includes(this.search.toLowerCase())
          || this.lookupTable[graphic.template_id]?.title?.toLowerCase()?.includes(this.search.toLowerCase())
        )
        : true
      ));
    },

    splitButtonOptions() {
      const groups = this.$store.state.production.production.timeline.filter((item) => (
        item.layout === 'graphics'
      ));

      const options = groups.map((group) => ({
        key: group.timeline_id,
        label: group.title,
        onClick: () => {
          this.addSelectedToGroup('Graphics', group.timeline_id);
        },
      }));

      if (options.length) {
        options.push({ divider: true });
      }

      // Option to create a new group and add to that instead
      options.push({
        key: 'new',
        label: '+ New Group',
        onClick: async () => {
          const group = await this.$store.dispatch('addGraphicsGroup', {
            posts: this.selected.map((item) => this.clone(item)),
          });

          this.selected = [];

          this.addSelectedToGroup('Graphics', group.timeline_id);
        },
      });

      return options;
    },

    owner() {
      return this.$store.state.production.production.owner;
    },

    lookupTable() {
      return this.$store.state.graphics.templatesLookupTable;
    },
  },

  methods: {
    ...mapActions([
      'changeGraphicTags',
      'changeGraphicsSearch',
    ]),

    startDrag(e) {
      const { item } = e;
      const { templateId } = item.dataset;

      const grabGroup = !!this.selected.find((t) => t._id === parseInt(templateId, 10));
      this.$store.dispatch('setDragType', {
        type: 'graphic',
        group: grabGroup ? this.selected.map(this.clone) : null,
      });
    },

    deleteConfirm(graphic) {
      this.selectedGraphic = graphic;
      this.showAlert = true;
    },

    duplicateGraphic(graphic, index) {
      this.$store.dispatch('duplicateGraphic', graphic);
      this.$refs.dropdown[index].hideDropdown();
    },

    async deleteGraphic() {
      this.$store.dispatch('startLoader');

      try {
        await this.$store.dispatch('deleteGraphic', this.selectedGraphic._id);
        this.successToast('Graphic deleted.');
      } catch (err) {
        this.errorToast('There was an error deleting this graphic. Please try again.');
      } finally {
        this.showAlert = false;
        this.selectedGraphic = null;
        this.$store.dispatch('stopLoader');
      }
    },

    clone(original) { // Called when dragging or adding to playlist
      const clone = pick(original, ['_id', 'template_id', 'title', 'meta']);

      clone.timeline_id = uuid.v1();
      clone.displayTime = 5000;
      clone.transition = 'Cut'; // legacy but keep for backwards compatibility during testing
      clone.layout = 'graphic';
      clone.posts = [{ timeline_id: clone.timeline_id }];
      clone.fields = original.fields ? structuredClone(original.fields) : {};

      return clone;
    },
  },
};
</script>
